import type {
  ArchivedDriver,
  ArchivedDriverMatch,
  Driver,
  DriverAttachment,
  DriverCreateInput,
  DriverDetails,
  DriverUpdateInput,
} from "../domain";
import type {
  ArchivedDriverDto,
  ArchivedDriverMatchDto,
  DriverCreateDto,
  DriverDocumentDto,
  DriverDto,
  DriverUpdateDto,
} from "./driverDtos";

/** `2030-01-15T00:00:00.000Z` -> `2030-01-15` (the domain keeps dates only). */
function toDate(value: string): string {
  return value.slice(0, 10);
}

const MIME_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
};

// The backend keeps no original file name or size: the name is the stored
// file's own (last URL segment), and the size stays unknown.
export function driverAttachmentFromDto(
  dto: DriverDocumentDto,
): DriverAttachment {
  const extension = dto.extension.toLowerCase();
  const lastSegment = dto.fileUrl.split("?")[0]?.split("/").pop() ?? "";
  return {
    id: dto.id,
    fileName: lastSegment.includes(".")
      ? decodeURIComponent(lastSegment)
      : `${dto.id}.${extension}`,
    mimeType: MIME_TYPES[extension] ?? "application/octet-stream",
    sizeBytes: null,
    uploadedAt: dto.createdAt,
    url: dto.fileUrl,
  };
}

export function driverDetailsFromDto(dto: DriverDto): DriverDetails {
  return {
    ...driverFromDto(dto),
    username: dto.username,
    email: dto.email,
    mobileNumber1: dto.phoneNumber1,
    mobileNumber2: dto.phoneNumber2 ?? null,
    rfid: dto.rfidTag ?? null,
    licenseIssueDate:
      dto.licenseIssueDate === null || dto.licenseIssueDate === undefined
        ? null
        : toDate(dto.licenseIssueDate),
    branch: dto.branch ?? null,
    attachments: (dto.documents ?? []).map(driverAttachmentFromDto),
  };
}

export function driverFromDto(dto: DriverDto): Driver {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    avatarUrl: dto.profilePictureUrl ?? null,
    nationalId: dto.nationalId,
    licenseNumber: dto.licenseNumber,
    licenseExpiryDate: toDate(dto.licenseExpiryDate),
    status: dto.status,
    // The backend has no assignment data yet.
    isAssigned: false,
    company: dto.account ?? null,
  };
}

export function driverCreateToDto(input: DriverCreateInput): DriverCreateDto {
  return {
    ...(input.company === null ? {} : { accountId: input.company.id }),
    branchId: input.branch.id,
    nameAr: input.nameAr,
    nameEn: input.nameEn,
    phoneNumber1: input.mobileNumber1,
    ...(input.mobileNumber2 === undefined
      ? {}
      : { phoneNumber2: input.mobileNumber2 }),
    email: input.email,
    username: input.username,
    nationalId: input.nationalId,
    ...(input.rfid === undefined ? {} : { rfidTag: input.rfid }),
    licenseNumber: input.licenseNumber,
    licenseIssueDate: input.licenseIssueDate,
    licenseExpiryDate: input.licenseExpiryDate,
  };
}

export function driverUpdateToDto(input: DriverUpdateInput): DriverUpdateDto {
  return {
    ...(input.branch === undefined ? {} : { branchId: input.branch.id }),
    ...(input.nameAr === undefined ? {} : { nameAr: input.nameAr }),
    ...(input.nameEn === undefined ? {} : { nameEn: input.nameEn }),
    ...(input.mobileNumber1 === undefined
      ? {}
      : { phoneNumber1: input.mobileNumber1 }),
    ...(input.mobileNumber2 === undefined
      ? {}
      : { phoneNumber2: input.mobileNumber2 }),
    ...(input.rfid === undefined ? {} : { rfidTag: input.rfid }),
    ...(input.licenseIssueDate === undefined
      ? {}
      : { licenseIssueDate: input.licenseIssueDate }),
    ...(input.licenseExpiryDate === undefined
      ? {}
      : { licenseExpiryDate: input.licenseExpiryDate }),
    ...(input.nationalId === undefined ? {} : { nationalId: input.nationalId }),
    ...(input.licenseNumber === undefined
      ? {}
      : { licenseNumber: input.licenseNumber }),
    ...(input.status === undefined ? {} : { status: input.status }),
  };
}

export function archivedDriverFromDto(dto: ArchivedDriverDto): ArchivedDriver {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    avatarUrl: dto.profilePictureUrl ?? null,
    username: dto.username,
    email: dto.email,
    company: dto.account ?? null,
    archivedAt: dto.archivedAt,
  };
}

export function archivedDriverMatchFromDto(
  dto: ArchivedDriverMatchDto,
): ArchivedDriverMatch {
  return {
    id: dto.id,
    nameEn: dto.nameEn ?? "",
    nameAr: dto.nameAr ?? "",
    archivedAt: dto.deletedAt ?? dto.archivedAt ?? null,
  };
}
